/**
 * Project: Aniatek
 * Author: Carlos A. Loaiza | Connect & Launch
 * Tel: +52 (667) 247 - 6316
 * Intellectual Property: All rights reserved (c) 2026
 */

<template>
  <div class="register-wrapper">
    
    <div v-if="isLanding && registrationSuccess" class="success-payment-view">
      <div class="success-icon">
        <i class="fas fa-check-circle"></i>
      </div>
      <h3>¡Cuenta creada con éxito!</h3>
      <p>Tu usuario ha sido registrado. Para activar tu membresía y comenzar a cotizar ahora mismo, realiza el pago seguro.</p>
      
      <button class="stripe-btn" @click="goToStripe">
        <span>Pagar y Activar Demo</span>
        <i class="fas fa-arrow-right"></i>
      </button>
      
      <p class="secure-note">
        <i class="fas fa-lock"></i> Pagos procesados de forma segura por Stripe
      </p>
    </div>

    <form v-else @submit.prevent="handleRegister" class="auth-form">

        <label>Nombre completo</label>
        <input type="text" placeholder="Ej. Juan Pérez López" v-model="formData.name" class="form-control"
            :class="{ 'input-error': errors.name }" @input="errors.name = false" required />
        <p v-if="errors.name" class="error-message">
            Ingresa nombre y apellidos (mínimo 3 letras).
        </p>

        <label>Estado</label>
        <select v-model="formData.state" @change="onCambioEstado" class="form-control"
            :class="{ 'input-error': errors.state }" required>
            <option value="" disabled>Selecciona tu estado</option>
            <option v-for="estadoObj in estadosData.estados" :key="estadoObj.nombre" :value="estadoObj.nombre">
                {{ estadoObj.nombre }}
            </option>
        </select>
        <p v-if="errors.state" class="error-message">Selecciona un estado.</p>

        <label>Ciudad / Municipio</label>
        <select v-model="formData.city" :disabled="!formData.state" class="form-control"
            :class="{ 'input-error': errors.city }" @change="errors.city = false" required>
            <option value="" disabled>
                {{ formData.state ? 'Selecciona tu ciudad' : 'Primero selecciona un estado' }}
            </option>
            <option v-for="municipio in municipiosDisponibles" :key="municipio" :value="municipio">
                {{ municipio }}
            </option>
        </select>
        <p v-if="errors.city" class="error-message">Selecciona una ciudad.</p>

        <label>Domicilio (calle y número)</label>
        <input type="text" placeholder="Calle, número, colonia..." v-model="formData.address" class="form-control"
            :class="{ 'input-error': errors.address }" @input="errors.address = false" required />
        <p v-if="errors.address" class="error-message">
            Por favor ingresa tu calle y número.
        </p>

        <label>Teléfono</label>
        <input type="text" placeholder="10 dígitos" v-model="formData.phone" maxlength="10" class="form-control"
            :class="{ 'input-error': errors.phone }" @input="errors.phone = false" required />
        <p v-if="errors.phone" class="error-message">
            El teléfono debe tener 10 números.
        </p>

        <label>Correo</label>
        <input type="email" placeholder="Ingresa tu correo" v-model="formData.email" class="form-control"
            :class="{ 'input-error': errors.email }" @input="errors.email = false" required />
        <p v-if="errors.email" class="error-message">
            Por favor ingresa un correo válido.
        </p>

        <label>Contraseña</label>
        <input type="password" placeholder="••••••••" v-model="formData.password" class="form-control"
            :class="{ 'input-error': errors.password }" @input="errors.password = false" required />
        <p v-if="errors.password" class="error-message">
            Mínimo 6 caracteres.
        </p>

        <div class="terms-container">
            <input type="checkbox" id="terms" v-model="acceptedTerms" class="terms-checkbox" />
            <label for="terms" class="terms-label">
                Acepto los
                <a href="/terminos.pdf" download class="terms-link" @click.stop>
                    términos y condiciones
                </a>
            </label>
        </div>

        <div class="links-group" v-if="!isLanding">
            <div class="link" @click="$emit('change-view', 'login')">
                Volver al login
            </div>
        </div>

        <p v-if="serverError" class="server-error">{{ serverError }}</p>

        <button type="submit" :disabled="isLoading || !acceptedTerms">
            {{ isLoading ? 'Creando cuenta...' : 'Crear cuenta' }}
        </button>
    </form>
  </div>
</template>


<script>
import userService from '@/services/userService';
import dataEstadosMunicipios from '@/data/estados_municipios.json';
import pagosService from '@/services/pagosService';

export default {
    name: 'RegisterForm',
    props: {
        isLanding: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isLoading: false,
            cargando: false,
            errorMsg: '',
            serverError: '',
            acceptedTerms: false,
            registrationSuccess: false,
            estadosData: dataEstadosMunicipios,
            formData: {
                name: '',
                email: '',
                password: '',
                state: '',
                city: '',
                address: '',
                phone: ''
            },
            errors: {
                name: false,
                email: false,
                password: false,
                state: false,
                city: false,
                address: false,
                phone: false
            }
        };
    },
    computed: {
        municipiosDisponibles() {
            if (!this.formData.state) return [];
            const estadoEncontrado = this.estadosData.estados.find(
                e => e.nombre === this.formData.state
            );
            return estadoEncontrado ? estadoEncontrado.municipios.map(m => m.nombre) : [];
        }
    },
    methods: {
        onCambioEstado() {
            this.formData.city = '';
            this.errors.state = false;
        },

        async goToStripe() {
            this.cargando = true;
            this.errorMsg = '';
            
            try {
                const response = await pagosService.crearSesionDePago();
                
                if (response.data.url) {
                    window.location.href = response.data.url; 
                } else {
                    this.errorMsg = "No se recibió el enlace de pago.";
                }
                
            } catch (error) {
                console.error("Error iniciando pago:", error);
                this.errorMsg = "Error al conectar con el servidor de pagos.";
                this.cargando = false;
            }finally {
            this.cargando = false;
            }
        },

        validateForm() {
            let isValid = true;
            this.serverError = '';

            if (!this.formData.name || this.formData.name.trim().length < 3) {
                this.errors.name = true; isValid = false;
            }
            if (!this.formData.state) {
                this.errors.state = true; isValid = false;
            }
            if (!this.formData.city) {
                this.errors.city = true; isValid = false;
            }
            if (!this.formData.address || this.formData.address.trim().length < 5) {
                this.errors.address = true; isValid = false;
            }
            const phoneRegex = /^[0-9]{10}$/;
            if (!this.formData.phone || !phoneRegex.test(this.formData.phone)) {
                this.errors.phone = true; isValid = false;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!this.formData.email || !emailRegex.test(this.formData.email)) {
                this.errors.email = true; isValid = false;
            }
            if (!this.formData.password || this.formData.password.length < 6) {
                this.errors.password = true; isValid = false;
            }
            return isValid;
        },

        splitName(fullName) {
            const parts = fullName.trim().split(' ');
            let nombre = parts[0];
            let ape_pat = parts.length > 1 ? parts[parts.length - 2] : '.';
            let ape_mat = parts.length > 2 ? parts[parts.length - 1] : '.';

            if (parts.length === 2) {
                ape_pat = parts[1];
                ape_mat = '.';
            }
            if (parts.length >= 3) {
                nombre = parts.slice(0, parts.length - 2).join(' ');
            }

            return { nombre, ape_pat, ape_mat };
        },

        async handleRegister() {
            if (!this.validateForm()) return;

            const id_instalador = 2;
            this.isLoading = true;

            const names = this.splitName(this.formData.name);

            const payload = {
                nombre: names.nombre,
                ape_pat: names.ape_pat,
                ape_mat: names.ape_mat,
                correo: this.formData.email,
                contraseña: this.formData.password,
                telefono: this.formData.phone,
                estado: this.formData.state,
                ciudad: this.formData.city,
                domicilio: this.formData.address, 
                id_rol: id_instalador,
            };

            try {
                const response = await userService.createUser(payload);
                const usuarioData = response.data;
                localStorage.setItem('userData', JSON.stringify(usuarioData));

                if (this.isLanding) {
                    this.registrationSuccess = true;
                } else {
                    if (this.$router) {
                        this.$router.replace('/Bienvenida');
                    } else {
                        window.location.href = '/Bienvenida';
                    }
                }

            } catch (error) {
                console.error("Error en registro:", error);
                if (error.response && error.response.data) {
                    const djangoErrors = error.response.data;
                    if (djangoErrors.correo) {
                        this.serverError = "Este correo ya está registrado.";
                    } else if (djangoErrors.error) {
                        this.serverError = djangoErrors.error;
                    } else {
                        this.serverError = "Error al registrar. Verifica los datos.";
                    }
                } else {
                    this.serverError = "Error de conexión con el servidor.";
                }
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>

<style scoped>
.register-wrapper {
    width: 100%;
}

.auth-form {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 5px;
}

label {
    font-size: 14px;
    margin-top: 10px;
}

.form-control {
    width: 100%;
    height: 42px;
    background-color: #fff;
    border: 1px solid #ccc;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .05);
    border-radius: 8px;
    padding: 0 10px;
    font-size: 14px;
    color: #333;
    outline: none;
    box-sizing: border-box;
    transition: all .3s ease;
}

.form-control:focus {
    outline: none;
    border-color: #29b9f6;
    box-shadow: 0 0 0 3px rgba(47, 141, 183, .2);
}

.form-control:disabled {
    background-color: #f0f0f0;
    color: #777;
}

.input-error {
    border-color: red;
}

.error-message {
    color: red;
    font-size: 12px;
    margin-top: -4px;
}

button {
    margin-top: 20px;
    height: 48px;
    background: #29b9f6;
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
}

button:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.terms-container {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
}

.terms-checkbox {
    width: 20px;
    height: 20px;
}

.terms-label {
    font-size: 13px;
}

.terms-link {
    color: #0056b3;
    font-weight: 600;
    text-decoration: underline;
}

.links-group {
    margin-top: 10px;
}

.link {
    font-size: 13px;
    color: #0056b3;
    cursor: pointer;
    text-decoration: underline;
}

.server-error {
    margin-top: 10px;
    background: #e55050;
    color: white;
    padding: 10px;
    border-radius: 6px;
    text-align: center;
    font-size: 14px;
}

.success-payment-view {
    text-align: center;
    padding: 20px 10px;
    animation: fadeIn 0.5s ease;
}

.success-icon {
    font-size: 50px;
    color: #25D366;
    margin-bottom: 20px;
}

.success-payment-view h3 {
    color: #0a0a0a;
    font-size: 22px;
    margin-bottom: 10px;
    font-weight: 700;
}

.success-payment-view p {
    color: #555;
    margin-bottom: 25px;
    font-size: 14px;
    line-height: 1.5;
}

.stripe-btn {
    background: #635bff;
    color: white;
    width: 100%;
    padding: 15px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: none;
    cursor: pointer;
    transition: background 0.3s;
    margin-top: 10px;
}

.stripe-btn:hover {
    background: #4b45c6;
}

.secure-note {
    margin-top: 15px !important;
    font-size: 12px !important;
    color: #888 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
}

@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}
</style>